import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Heart, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../App';

export default function DonationSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('polling');
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    const poll = async () => {
      try {
        const res = await api.get(`/api/payments/status/${sessionId}`);
        if (res.payment_status === 'paid') {
          setPaymentData(res);
          setStatus('paid');
          return;
        }
        if (res.status === 'expired') { setStatus('expired'); return; }
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else { setStatus('timeout'); }
      } catch {
        if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else { setStatus('error'); }
      }
    };
    poll();
  }, [sessionId, attempts]);

  if (status === 'polling') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <Loader2 className="w-12 h-12 animate-spin text-[hsl(var(--gs-saffron))] mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2" style={{fontFamily: 'Space Grotesk'}}>Processing your donation...</h2>
        <p className="text-[hsl(var(--muted-foreground))]">Please wait while we confirm your payment.</p>
      </div>
    );
  }

  if (status !== 'paid') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h2 className="text-xl font-semibold mb-2" style={{fontFamily: 'Space Grotesk'}}>Donation {status}</h2>
        <p className="text-[hsl(var(--muted-foreground))] mb-4">There was an issue processing your donation.</p>
        <Link to="/donations"><Button>Try Again</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="donation-success-page">
      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-8 h-8 text-green-600" />
      </div>
      <h1 className="text-3xl font-semibold mb-2" style={{fontFamily: 'Space Grotesk'}}>Thank you for your donation!</h1>
      <p className="text-[hsl(var(--muted-foreground))] mb-6">Your generous contribution helps us serve the community.</p>
      {paymentData && (
        <div className="inline-block p-6 rounded-xl bg-[hsl(var(--gs-saffron-soft))] mb-8">
          <div className="text-sm text-[hsl(var(--muted-foreground))]">Amount donated</div>
          <div className="text-4xl font-bold text-[hsl(var(--gs-saffron))] tabular-nums" style={{fontFamily: 'Space Grotesk'}}>£{(paymentData.amount_total / 100).toFixed(2)}</div>
        </div>
      )}
      <div className="flex flex-wrap gap-3 justify-center">
        <Link to="/"><Button variant="outline">Back to Home</Button></Link>
        <Link to="/events"><Button className="bg-[hsl(var(--primary))] text-white">Browse Events</Button></Link>
      </div>
    </div>
  );
}
